import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import ClientLogo from './ClientLogo';

interface Client {
  key: number;
  title: string;
  role: string;
  image: string;
  link: string;
  appLink?: string;
  whatWeDid: string;
}

interface ClientModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

// Create modal root container once - this runs immediately when module loads
const getModalRoot = (): HTMLDivElement => {
  let root = document.getElementById('modal-root') as HTMLDivElement;
  
  if (!root && typeof document !== 'undefined') {
    root = document.createElement('div');
    root.id = 'modal-root';
    // Ensure it's positioned correctly and has no transforms
    root.style.position = 'fixed';
    root.style.top = '0';
    root.style.left = '0';
    root.style.width = '100%';
    root.style.height = '100%';
    root.style.pointerEvents = 'none';
    root.style.zIndex = '99999';
    root.style.isolation = 'isolate'; // Create new stacking context
    document.documentElement.appendChild(root);
  }
  
  return root!;
};

const ClientModal = ({ client, isOpen, onClose }: ClientModalProps) => {
  const scrollPositionRef = useRef<number>(0);
  const modalRootRef = useRef<HTMLDivElement | null>(null);

  // Initialize modal root on mount
  useEffect(() => {
    modalRootRef.current = getModalRoot();
  }, []);

  // Prevent body scroll when modal is open and preserve scroll position
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position BEFORE locking
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      scrollPositionRef.current = scrollY;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';
      
      return () => {
        const savedScroll = scrollPositionRef.current;
        
        // Restore body styles first
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.left = '';
        document.body.style.right = '';
        
        // Restore scroll position after DOM updates
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Temporarily disable smooth scrolling
          const html = document.documentElement;
          const originalScrollBehavior = html.style.scrollBehavior;
          html.style.scrollBehavior = 'auto';
          
          // Restore scroll position
          window.scrollTo(0, savedScroll);
          document.documentElement.scrollTop = savedScroll;
          
          // Double-check after another frame to ensure it stuck
          requestAnimationFrame(() => {
            const currentScroll = window.scrollY || document.documentElement.scrollTop;
            if (Math.abs(currentScroll - savedScroll) > 1) {
              window.scrollTo(0, savedScroll);
              document.documentElement.scrollTop = savedScroll;
            }
            // Restore original scroll behavior
            html.style.scrollBehavior = originalScrollBehavior;
          });
        });
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !client) return null;
  
  // Get modal root (create if needed)
  const modalRoot = modalRootRef.current || getModalRoot();
  if (!modalRoot) return null;

  // Make "what we did" more casual/nonchalant
  const casualWhatWeDid = client.whatWeDid
    .replace(/^Built /i, 'built ')
    .replace(/^Developed /i, 'the development of ')
    .replace(/^Created /i, 'the creation of ')
    .replace(/^Designed and developed /i, 'the development of ')
    .toLowerCase();

  // Get detailed description for specific clients
  const getDetailedDescription = (title: string): string => {
    if (title === 'Mobispaw') {
      return 'Through the KMG Vectoral System, we built Mobispaw\'s internal operations platform and client booking system. It centralizes booking, scheduling, staff coordination, payments, client records, and communication in one place. Clients can book online, get reminders, and follow pet updates, while the team manages pet profiles, staff schedules, and automated workflows. The system reduces manual work, minimizes missed appointments, and supports growth with review boosters and automated marketing tools.';
    }
    if (title === 'Spikeball') {
      return 'Through the KMG Vectoral System, we built Spikeball\'s mobile app and internal systems to increase community involvement and streamline operations. The app powers community engagement with events, play updates, and participation tracking, while collecting valuable activity data. That data feeds an internal dashboard where teams can process insights, monitor growth, and turn community behavior into clear, actionable decisions.';
    }
    if (title === 'The National Fix') {
      return 'Through the KMG Vectoral System, we built The National Fix\'s enterprise facilities management platform that handles work orders, asset management, preventative maintenance, and provider coordination at scale. The system centralizes facility operations across thousands of locations, enabling teams to track maintenance requests, schedule service providers, manage assets and equipment, automate workflows, and generate comprehensive analytics. It streamlines communication between facilities, field teams, and vendors while providing real-time visibility into operations, costs, and compliance across the entire portfolio.';
    }
    if (title === 'Customaps') {
      return 'Through the KMG Vectoral System, we maintained and enhanced Customaps\' existing software platform that allows clients to order and customize large-scale real-world maps for printing. The system enables customers to personalize map designs, select regions, customize styling and details, and place orders for high-quality printed maps. We ensured the platform remains stable, performant, and user-friendly while supporting the full order-to-print workflow.';
    }
    if (title === 'Priveda Capital') {
      return 'Through the KMG Vectoral System, we maintained and optimized Priveda Capital\'s core database infrastructure, ensuring reliable data management across their rapidly growing real estate portfolio. We implemented robust data integrity protocols, optimized query performance, and established automated backup and recovery systems. The infrastructure supports portfolio tracking, tenant management, financial reporting, and property analytics while maintaining high availability and data security standards as the company scales to thousands of units.';
    }
    if (title === 'Levine Bros') {
      return 'Through the KMG Vectoral System, we built Levine Bros\' comprehensive operations platform that transformed their field service management. The system includes intelligent dispatch and scheduling software that replaces spreadsheets with visual workforce management, allowing the office to assign plumbers to shifts, view weekly workload distribution, and adjust schedules in real-time. We implemented automated emergency dispatch escalation that routes 24/7 emergency calls through priority-based contact chains, eliminating manual after-hours coordination. The platform features a centralized technician management database with contact information, language capabilities, and performance tracking, plus role-based access control for staff and admin management. An operational analytics dashboard tracks key metrics including call volumes, emergency requests, customer growth, and plumber availability, giving management real-time visibility into operations. The system serves as the foundation for their expanding customer CRM capabilities.';
    }
    if (title === 'Chatzy') {
      return 'Through the KMG Vectoral System, we built Chatzy\'s AI-powered conversational platform that processes millions of conversations through intelligent automation. The system leverages natural language processing to understand and respond to user queries across multiple channels, enabling seamless multi-channel integration. We developed sophisticated AI capabilities that handle complex conversational flows, context management, and intent recognition, allowing the platform to deliver accurate and contextually relevant responses at scale. The infrastructure supports high-volume conversation processing while maintaining low latency and high reliability, making it suitable for enterprise-level deployment across various industries and use cases.';
    }
    if (title === 'Viridia Capital') {
      return 'Through the KMG Vectoral System, we built Viridia Capital\'s secure client management portal to modernize how investors interact with their accounts. The platform allows clients to access and monitor their portfolios, track performance, and manage transactions through a centralized dashboard. It provides real time visibility into balances, earnings, and investment activity while maintaining institutional grade security and transparency. The system gives Viridia a scalable digital interface for investor communication, portfolio oversight, and streamlined client management.';
    }
    if (title === 'CYG Finance') {
      return 'Through the KMG Vectoral System, we built CYG Finance\'s internal accounting workflow platform to automate and streamline their financial operations. The system centralizes incoming tasks from emails, calls, and client requests into a unified dashboard where accounting work can be automatically categorized, assigned, and tracked. It includes workflow automation for bookkeeping and accounts payable tasks, real time task management, and filtering tools that allow staff to quickly organize and process financial requests. The platform reduces manual coordination and gives management clear visibility into workload, task status, and operational efficiency across the accounting team.';
    }
    if (title === 'smarTAB') {
      return 'Through the KMG Vectoral System, we built TABCheck\'s field workflow platform to standardize and digitize operational procedures for technicians in the field. The application allows teams to follow structured digital checklists, capture job data, and document work completion directly from mobile devices. By replacing manual paperwork with guided task flows, the system improves consistency, accountability, and reporting across field operations. Managers gain real time visibility into task progress and completed inspections, allowing organizations to maintain standardized processes while coordinating work across distributed teams.';
    }
    return casualWhatWeDid;
  };

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative z-[10000] w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-8">
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 mb-8">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <ClientLogo image={client.image} title={client.title} size="sm" />
              <div className="mt-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  {client.title}
                </h2>
                <p className="text-base text-slate-500 mt-2">
                  {client.role}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-4 items-center lg:items-start mt-4 lg:mt-6">
              <p className="text-sm text-slate-500 italic leading-relaxed text-center lg:text-left max-w-xl">
                {client.title === 'Spikeball' && 'A backyard game turned global sports phenomenon. Featured on Shark Tank and ESPN, made the Inc. 500 list twice, and sold nearly $50 million worth of equipment in just a decade.'}
                {client.title === 'Mobispaw' && "Montreal's top-rated mobile pet grooming service. Brings the pet salon to your doorstep in a high-tech grooming van, ranking among the top pet groomers in Canada."}
                {client.title === 'The National Fix' && 'A full-service facilities maintenance powerhouse keeping thousands of client locations running smoothly. Handles nearly 8,000 facilities with 24/7 "Anyplace, Anytime, Anywhere" service.'}
                {client.title === 'Priveda Capital' && 'A fast-growing real estate investment firm that launched in 2024 and already amassed a portfolio of about 2,000 apartment units across Eastern Canada, including a $76.5 million acquisition.'}
                {client.title === 'Levine Bros' && 'A Montreal institution with over a century of service. Founded in 1922, this fourth-generation family business grew from a two-person operation into a fleet of 22 service trucks.'}
                {client.title === 'Customaps' && 'An innovative Montreal-born platform redefining map making. Built "the most complete website dedicated to maps", empowering users to design custom map posters, wallpapers, and digital maps.'}
                {client.title === 'CYG Finance' && 'A tech-driven accounting firm that acts as the outsourced finance department for 50+ companies, handling everything from bookkeeping and payroll to tax filings through a secure client portal.'}
                {client.title === 'Chatzy' && 'Chatzy processes millions of conversations through AI-powered automation'}
                {client.title === 'Viridia Capital' && 'Viridia Capital manages portfolios for high-net-worth clients'}
                {client.title === 'smarTAB' && 'smarTAB optimizes building performance across commercial facilities'}
                {!['Spikeball', 'Mobispaw', 'The National Fix', 'Priveda Capital', 'Levine Bros', 'Customaps', 'CYG Finance', 'Chatzy', 'Viridia Capital', 'smarTAB'].includes(client.title) && `${client.title} leverages technology to transform their operations`}
              </p>
            </div>
          </div>

          {/* What We Did */}
          <div className="p-6 bg-gradient-to-br from-kmg-primary/5 via-kmg-accent/5 to-kmg-primary/5 rounded-2xl border-2 border-kmg-primary/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-8 bg-gradient-to-b from-kmg-primary to-kmg-accent rounded-full"></div>
              <h3 className="text-xl font-semibold text-slate-900">
                <span className="text-kmg-primary">What we did</span>
              </h3>
            </div>
            <p className="text-slate-700 leading-relaxed text-base">
              {getDetailedDescription(client.title)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal to dedicated modal root container that bypasses all CSS transforms
  return createPortal(modalContent, modalRoot);
};

export default ClientModal;
